<template>
  <!-- Переключатели определяют обязательность полей публичной анкеты абитуриента. -->
  <v-card class="general-settings" elevation="0">
    <v-card-title class="general-settings__header">
      <div>
        <h2 class="general-settings__title">Обязательные поля для заполнения</h2>
        <p class="general-settings__description">
          Выберите сведения, которые абитуриент должен обязательно указать в анкете.
        </p>
      </div>
    </v-card-title>

    <v-card-text class="general-settings__content">
      <v-alert
        v-if="error"
        class="general-settings__alert"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <div v-if="loading" class="general-settings__loading">
        <v-skeleton-loader v-for="index in 5" :key="index" type="list-item-two-line" />
      </div>

      <div v-else class="general-settings__groups">
        <section
          v-for="group in groups"
          :key="group.title"
          class="general-settings__group"
        >
          <h3 class="general-settings__group-title">{{ group.title }}</h3>

          <div class="general-settings__list">
            <div
              v-for="field in group.fields"
              :key="field.name"
              class="general-settings__item"
            >
              <div class="general-settings__item-text">
                <div class="general-settings__item-title">{{ field.title }}</div>
                <div v-if="field.description" class="general-settings__item-description">
                  {{ field.description }}
                </div>
              </div>

              <v-checkbox
                v-model="settings[field.name]"
                class="general-settings__checkbox"
                color="primary"
                density="comfortable"
                hide-details
                :aria-label="field.title"
                :disabled="saving[field.name]"
                :loading="saving[field.name]"
                @update:model-value="saveSetting(field.name, $event)"
              />
            </div>
          </div>
        </section>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'GeneralSection',
  data() {
    return {
      loading: true,
      error: '',
      settings: {},
      saving: {},
      groups: [
        {
          title: 'Данные абитуриента',
          fields: [
            {
              name: 'applicantNeedParent',
              title: 'Данные родителей для несовершеннолетних',
              description: 'Обязательны, если абитуриент не достиг совершеннолетия.'
            },
            {
              name: 'applicantNeedParentAlways',
              title: 'Данные родителей для всех абитуриентов',
              description: 'Обязательны независимо от возраста абитуриента.'
            },
            { name: 'applicantNeedSnils', title: 'СНИЛС' },
            { name: 'applicantNeedEmail', title: 'Электронная почта' },
            { name: 'applicantRegAddress', title: 'Адрес регистрации абитуриента' }
          ]
        },
        {
          title: 'Документ об образовании',
          fields: [
            { name: 'edYear', title: 'Год окончания учебного заведения' },
            { name: 'edDate', title: 'Дата получения аттестата' },
            { name: 'edNumber', title: 'Номер аттестата или свидетельства' },
            { name: 'gia', title: 'Результаты ГИА' },
            { name: 'avg', title: 'Оценки аттестата' }
          ]
        }
      ]
    };
  },
  created() {
    this.loadSettings();
  },
  methods: {
    loadSettings() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getGeneralSettings'
        )
        .then((response) => {
          this.settings = response.data.settings || {};
        })
        .catch((error) => {
          this.error = 'Не удалось загрузить настройки обязательных полей.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveSetting(name, enabled) {
      // Каждый флаг сохраняется сразу; при ошибке интерфейс возвращает
      // предыдущее значение, чтобы не показывать несохранённое правило.
      const previousValue = !enabled;
      this.saving = { ...this.saving, [name]: true };
      this.error = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/setGeneralSetting',
          new URLSearchParams({
            name,
            enabled: enabled ? '1' : '0'
          })
        )
        .catch((error) => {
          this.settings[name] = previousValue;
          this.error = 'Не удалось сохранить настройку. Изменение отменено.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.saving = { ...this.saving, [name]: false };
        });
    }
  }
};
</script>

<style scoped>
.general-settings {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.general-settings__header {
  padding: 24px 28px 18px;
  border-bottom: 1px solid #edf0f4;
  white-space: normal;
}

.general-settings__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
}

.general-settings__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.general-settings__content {
  padding: 24px 28px 28px;
}

.general-settings__alert {
  margin-bottom: 20px;
}

.general-settings__groups {
  display: grid;
  gap: 28px;
}

.general-settings__group-title {
  margin: 0 0 10px;
  color: #475467;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.general-settings__list {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}

.general-settings__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 64px;
  padding: 10px 16px 10px 18px;
  border-bottom: 1px solid #edf0f4;
}

.general-settings__item:last-child {
  border-bottom: 0;
}

.general-settings__item:hover {
  background: #f8fafc;
}

.general-settings__item-title {
  color: #344054;
  font-weight: 500;
}

.general-settings__item-description {
  margin-top: 2px;
  color: #667085;
  font-size: 13px;
}

.general-settings__checkbox {
  flex: 0 0 auto;
}

@media (max-width: 600px) {
  .general-settings__header,
  .general-settings__content {
    padding-right: 18px;
    padding-left: 18px;
  }
}
</style>
